import CancelModalButton from 'components/Custom/CancelModalButton'
import CustomModalFooter from 'components/Custom/CustomModalFooter'
import CustomModalHeader from 'components/Custom/CustomModalHeader'
import SubmitModalButton from 'components/Custom/SubmitModalButton'
import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useDispatch } from 'react-redux'
import { Card, FormGroup, Input, Modal, ModalBody } from 'reactstrap'
import { fetchUserStores } from 'store/Forms/actions'
import { submitForm } from 'store/Forms/actions'
import { setNewFormFields } from 'store/Forms/actions'
import { fetchForm } from 'store/Forms/actions'
import SignatureCanvas from 'react-signature-canvas'
import { setFiles } from 'store/Forms/actions'
import { resetFiles } from 'store/Forms/actions'
import { headingSize } from './FormFieldsData'
import Select from '../Select'
import { clientBaseUrl } from 'utils/AxiosInstance'

const dataURLtoFile = (dataURL, filename) => {
  const arr = dataURL.split(',')
  const mime = arr[0].match(/:(.*?);/)[1]
  const bstr = atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  return new File([u8arr], filename, { type: mime })
}

function TakeFormModal({
  formId,
  showTakeFormModal,
  setShowTakeFormModal,
  setHasSubmissions,
  onFormSubmitted,
}) {
  const dispatch = useDispatch()
  const { newFormFields, userStores, files } = useSelector(
    (store) => store.form
  )
  const { disableLocationForms } = useSelector((store) => store.generalConfig)

  const [currentTab, setCurrentTab] = useState(1)
  const [selectedStores, setSelectedStores] = useState([])
  const [unansweredFields, setUnansweredFields] = useState([])

  const signatureRefs = useRef({})

  const clearSignature = (index) => {
    const signaturePad = signatureRefs.current[index]
    if (signaturePad) {
      signaturePad.clear()
    } else {
      console.warn(`No signature pad found for index: ${index}`)
    }
  }

  const unansweredFieldsExist = () => {
    const notAnswered = newFormFields.filter(
      (field) => field.required && !field.answer && !field.answered
    )

    if (notAnswered && notAnswered.length) {
      setUnansweredFields(notAnswered.map((field) => field.index))
      return true
    }

    return false
  }

  const answerField = (answer, index) => {
    const currentIndex = newFormFields.findIndex(
      (element) => element.index === index
    )
    const field = newFormFields.find((field) => field.index === index)

    if (currentIndex !== -1) {
      const updatedFormFields = [...newFormFields]
      updatedFormFields[currentIndex] = { ...field, answer }

      dispatch(setNewFormFields(updatedFormFields))
    }
  }

  const selectOption = (optionId, index) => {
    const currentIndex = newFormFields.findIndex(
      (element) => element.index === index
    )
    const field = newFormFields.find((field) => field.index === index)

    if (currentIndex !== -1) {
      const updatedFormFields = [...newFormFields]

      const updatedOptions = field.options.map((option) =>
        option.id === optionId
          ? { ...option, checked: !option.checked }
          : option
      )

      const answered = Boolean(
        updatedOptions?.filter((option) => option.checked)?.length
      )

      updatedFormFields[currentIndex] = {
        ...field,
        options: updatedOptions,
        answered,
      }

      dispatch(setNewFormFields(updatedFormFields))
    }
  }

  const selectDropdownOption = (selectedOptionIds, index) => {
    const currentIndex = newFormFields.findIndex(
      (element) => element.index === index
    )
    const field = newFormFields.find((field) => field.index === index)

    if (currentIndex !== -1) {
      const updatedFormFields = [...newFormFields]

      // Update the options based on selectedOptionIds
      const updatedOptions = field.options.map((option) => ({
        ...option,
        checked: selectedOptionIds.includes(option.id),
      }))

      const answered = Boolean(
        updatedOptions?.filter((option) => option.checked)?.length
      )

      updatedFormFields[currentIndex] = {
        ...field,
        options: updatedOptions,
        answered,
      }

      dispatch(setNewFormFields(updatedFormFields))
    }
  }

  const selectRadio = (optionId, index) => {
    const currentIndex = newFormFields.findIndex(
      (element) => element.index === index
    )
    const field = newFormFields.find((field) => field.index === index)
    const foundOption = field.options.find((option) => option.id === optionId)

    if (currentIndex !== -1) {
      const updatedFormFields = [...newFormFields]

      const updatedOptions = field.options.map((option) => {
        if (option.id === foundOption.id) {
          return { ...foundOption, checked: !foundOption.checked }
        } else {
          return { ...option, checked: false }
        }
      })

      const answered = Boolean(
        updatedOptions?.filter((option) => option.checked)?.length
      )

      updatedFormFields[currentIndex] = {
        ...field,
        options: updatedOptions,
        answered,
      }

      dispatch(setNewFormFields(updatedFormFields))
    }
  }

  const selectStore = (storeId) => {
    if (!selectedStores.includes(storeId)) {
      setSelectedStores([...selectedStores, storeId])
    } else {
      setSelectedStores(selectedStores.filter((id) => id !== storeId))
    }
  }

  useEffect(() => {
    dispatch(fetchForm(formId))
  }, [dispatch, formId])

  useEffect(() => {
    dispatch(fetchUserStores())
    dispatch(resetFiles())
  }, [dispatch])

  const renderBody = (field) => {
    switch (field.type) {
      case 'heading':
        return (
          <p
            style={{ fontSize: headingSize[field?.fontSize] }}
            className='border-bottom border-dark text-dark'
          >
            {field.text}
          </p>
        )
      case 'paragraph':
        return <p>{field.text}</p>
      case 'text':
      case 'number':
      case 'textarea':
      case 'date':
        return (
          <FormGroup className='ml-0 mb-0'>
            <label
              className='form-control-label'
              htmlFor={`field-${field.index}`}
            >
              {field.label}{' '}
              {field.required ? <span className='text-danger'>*</span> : null}
            </label>
            <Input
              key={field?.index}
              readOnly={field?.readOnly}
              disabled={field?.disabled}
              placeholder={field?.placeholder}
              defaultValue={field?.value}
              onChange={(e) => answerField(e.target.value, field.index)}
              id={`field-${field.index}`}
              type={
                field.inputType
                  ? field.inputType
                  : field.type === 'number'
                  ? 'number'
                  : field.type === 'text'
                  ? 'text'
                  : field.type === 'date'
                  ? 'date'
                  : 'textarea'
              }
              style={{
                height: field.type === 'textarea' ? 'auto' : '38px',
                resize: 'vertical',
              }}
              name={`field-${field.index}`}
              rows={field?.rows}
              min={field?.min}
              max={field?.max}
            />
            {field.helpText ? (
              <p style={{ fontSize: '14px' }}>{field.helpText}</p>
            ) : null}
          </FormGroup>
        )
      case 'checkbox':
      case 'radio':
        return (
          <div className='d-flex flex-column' style={{ gap: '0.5rem' }}>
            <label className='form-control-label' htmlFor='text'>
              {field.label}{' '}
              {field.required ? <span className='text-danger'>*</span> : null}
            </label>
            {field?.options?.map((option, index) => (
              <>
                {field.type === 'checkbox' ? (
                  <div
                    className='custom-control custom-checkbox'
                    style={{ zIndex: '0' }}
                  >
                    <input
                      className='custom-control-input'
                      id={`${field.index}${index}`}
                      type='checkbox'
                      checked={option.checked}
                      disabled={field?.disabled}
                      onChange={() => selectOption(option.id, field.index)}
                    />
                    <label
                      className='custom-control-label'
                      htmlFor={`${field.index}${index}`}
                    >
                      {option.label}
                    </label>
                  </div>
                ) : (
                  <div
                    className='d-flex align-items-center'
                    style={{ zIndex: 0 }}
                  >
                    <input
                      id={`radio-${option.label}`}
                      type='radio'
                      checked={option.checked}
                      style={{ marginRight: '0.5rem' }}
                      onChange={() => selectRadio(option.id, field.index)}
                    />
                    <label htmlFor={`radio-${option.label}`} className='mb-0'>
                      {option.label}
                    </label>
                  </div>
                )}
              </>
            ))}
            {field.helpText ? (
              <p style={{ fontSize: '14px' }}>{field.helpText}</p>
            ) : null}
          </div>
        )

      case 'select':
        const options = field.options
          ? field.options.map((field) => {
              return { id: field.id, name: field.label }
            })
          : []
        const selectedOptions = field?.options
          ?.filter((options) => options.checked === true)
          ?.map((field) => {
            return { id: field.id, name: field.label }
          })

        return (
          <>
            <label className='form-control-label' htmlFor='text'>
              {field.label}{' '}
              {field.required ? <span className='text-danger'>*</span> : null}
            </label>
            <Select
              options={options}
              selectedOptions={selectedOptions || []}
              onChange={(e) => {
                if (field?.readOnly || field?.disasbled) return
                const selectedOptionIds = e.map((option) => option.option.id)
                selectDropdownOption(selectedOptionIds, field.index)
              }}
              multi='true'
            />
            {field.helpText ? (
              <p style={{ fontSize: '14px' }}>{field.helpText}</p>
            ) : null}
          </>
        )

      case 'upload':
        if (field.inputType === 'attach') {
          // Admin-attached file - show view and download buttons only
          return (
            <FormGroup className='ml-0 mb-0'>
              <label role='button' className='form-control-label'>
                {field.label}{' '}
                {field.required ? <span className='text-danger'>*</span> : null}
              </label>
              {field?.fileUrl && field?.selectedFileName ? (
                <div
                  className='mt-2 p-2 border rounded'
                  style={{ backgroundColor: '#f8f9fa' }}
                >
                  <button
                    onClick={() => {
                      window.open(field?.fileUrl, '_blank')
                    }}
                    className='btn btn-info py-1 mr-3'
                  >
                    View <i className='fas fa-eye'></i>
                  </button>
                  <button
                    onClick={() => {
                      if (!field?.fileUrl) {
                        alert('No file URL available for download')
                        return
                      }

                      const url = clientBaseUrl()
                      const fileUrl = encodeURIComponent(field?.fileUrl)
                      const proxyUrl = `${url}forms/proxy-file?url=${fileUrl}`

                      const link = document.createElement('a')
                      link.href = proxyUrl
                      link.download =
                        field.selectedFileName || 'downloaded-file'
                      document.body.appendChild(link)
                      link.click()
                      document.body.removeChild(link)
                    }}
                    className='btn btn-success py-1 mr-3'
                  >
                    Download <i className='fas fa-download'></i>
                  </button>
                  <small className='text-success'>
                    <strong>File:</strong> {field.selectedFileName}
                  </small>
                </div>
              ) : (
                <p className='text-muted'>No file attached by admin.</p>
              )}
              {field.helpText ? (
                <p style={{ fontSize: '14px' }}>{field.helpText}</p>
              ) : null}
            </FormGroup>
          )
        } else {
          // Regular user file upload
          return (
            <FormGroup className='ml-0 mb-0'>
              <label
                role='button'
                className='form-control-label'
                htmlFor='upload'
              >
                {field.label}{' '}
                {field.required ? <span className='text-danger'>*</span> : null}
              </label>
              <Input
                key={field?.index}
                readOnly={field?.readOnly}
                disabled={field?.disabled}
                accept={field?.accept}
                id='upload'
                type='file'
                name='upload'
                role='button'
                onChange={(e) => {
                  answerField(true, field.index)
                  dispatch(
                    setFiles({ ...e.target.files, fieldIndex: field.index })
                  )
                }}
              />
              {field.helpText ? (
                <p style={{ fontSize: '14px' }}>{field.helpText}</p>
              ) : null}
            </FormGroup>
          )
        }

      case 'signature':
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'start',
            }}
          >
            <label
              role='button'
              className='form-control-label'
              style={{ marginBottom: '8px' }}
            >
              {field.label}
            </label>
            <SignatureCanvas
              ref={(el) => (signatureRefs.current[field.index] = el)}
              penColor='black'
              canvasProps={{
                width: field.width,
                height: field.height,
                style: {
                  borderBottom: '1px dotted black',
                  display: 'block',
                },
              }}
            />
            {field.helpText ? (
              <p style={{ fontSize: '14px' }}>{field.helpText}</p>
            ) : null}
            <div className='mt-2 d-flex align-items-center'>
              <button
                className='btn btn-danger'
                onClick={() => clearSignature(field.index)}
              >
                Clear
              </button>
            </div>
          </div>
        )

      default:
        return <span>{field.label}</span>
    }
  }

  return (
    <Modal
      toggle={() => setShowTakeFormModal(false)}
      isOpen={showTakeFormModal}
      centered
      size='xl'
    >
      <CustomModalHeader
        title={'Fill Form'}
        toggle={() => setShowTakeFormModal(false)}
      />
      <ModalBody>
        <Card className='p-4 d-flex flex-column' style={{ gap: '1rem' }}>
          <>
            {currentTab === 1 ? (
              newFormFields?.map((field) => (
                <div
                  style={
                    unansweredFields?.includes(field?.index)
                      ? {
                          border: '1px dotted red',
                          padding: '8px',
                          borderRadius: '0.5rem',
                        }
                      : {}
                  }
                  key={field.index}
                >
                  {renderBody(field)}
                  {unansweredFields?.includes(field?.index) ? (
                    <span className='text-danger'>This Field is Required!</span>
                  ) : null}
                </div>
              ))
            ) : (
              <>
                <h3>Is this related to any of your locations?</h3>
                <span>
                  Selecting a location improves issue management. Skip if the
                  issue is general.
                </span>

                {userStores?.map((store) => (
                  <div
                    key={store.store_id}
                    className='custom-control custom-checkbox mb-3'
                    style={{ zIndex: '0' }}
                  >
                    <input
                      className='custom-control-input'
                      id={`store-${store.store_id}`}
                      type='checkbox'
                      onChange={() => selectStore(store.store_id)}
                    />
                    <label
                      className='custom-control-label'
                      htmlFor={`store-${store.store_id}`}
                    >
                      {store.name}
                    </label>
                  </div>
                ))}
              </>
            )}
          </>
        </Card>
      </ModalBody>
      <CustomModalFooter>
        <SubmitModalButton
          customFn={() => {
            if (currentTab === 1) {
              const stop = unansweredFieldsExist()
              if (stop) {
                return
              } else {
                const signatureFiles = []

                Object.entries(signatureRefs.current).forEach(
                  ([index, signaturePad]) => {
                    if (signaturePad && !signaturePad.isEmpty()) {
                      const dataURL = signaturePad.toDataURL()
                      const file = dataURLtoFile(
                        dataURL,
                        `signature-${index}.png`
                      )
                      signatureFiles.push({
                        file,
                        fieldIndex: index,
                      })
                    } else if (signaturePad) {
                      alert(`Signature at index ${index} is empty!`)
                    }
                  }
                )

                signatureFiles.forEach(({ file, fieldIndex }) => {
                  dispatch(setFiles({ file, fieldIndex }))
                })

                if (disableLocationForms) {
                  dispatch(submitForm(formId, selectedStores)).then(() => {
                    setHasSubmissions((submissions) => submissions + 1)
                    onFormSubmitted && onFormSubmitted()
                  })
                  setShowTakeFormModal(false)
                } else {
                  setCurrentTab(2)
                }
              }
            } else {
              dispatch(submitForm(formId, selectedStores)).then(() => {
                setHasSubmissions((submissions) => submissions + 1)
                onFormSubmitted && onFormSubmitted()
              })
              setShowTakeFormModal(false)
            }
          }}
          text={currentTab === 1 && !disableLocationForms ? 'Next' : 'Submit'}
        ></SubmitModalButton>
        <CancelModalButton
          customFn={() => {
            setShowTakeFormModal(false)
          }}
        ></CancelModalButton>
      </CustomModalFooter>
    </Modal>
  )
}

export default TakeFormModal
